<template>
  <div ref="rootRef" class="lang-switcher">
    <div
      class="lang-switcher-trigger"
      @click.stop="toggleDropdown"
    ></div>
    <ul
      ref="dropdownRef"
      v-show="dropdownOpen"
      class="lang-switcher-dropdown"
      :style="dropdownStyle"
      @click.stop
    >
      <li
        v-for="opt in langOptions"
        :key="opt.code"
        class="lang-switcher-option"
        :class="{ 'is-active': curLang === opt.code }"
        @click="changeLang(opt.code)"
      >
        {{ opt.label }}
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import LanguageHook from "@hooks/language";

const { curLang, setLang, languageList } = LanguageHook();

const LANG_LABELS = {
  "en-US": "English",
  "id-ID": "Indonesia",
  "th-TH": "ไทย",
  "zh-CN": "简体中文",
};

const langOptions = computed(() =>
  languageList.map((code) => ({ code, label: LANG_LABELS[code] || code }))
);

const dropdownOpen = ref(false);
const rootRef = ref(null);
const dropdownRef = ref(null);
const dropdownStyle = ref({});

const VIEWPORT_EDGE_MARGIN = 10;

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
};

const closeDropdown = () => {
  dropdownOpen.value = false;
};

const changeLang = (code) => {
  setLang(code);
  dropdownOpen.value = false;
};

const alignDropdown = () => {
  const root = rootRef.value;
  const dropdown = dropdownRef.value;
  if (!root || !dropdown || !dropdownOpen.value) return;

  const rootRect = root.getBoundingClientRect();
  const trigger = root.querySelector(".lang-switcher-trigger");
  if (!trigger) return;

  const triggerRect = trigger.getBoundingClientRect();
  const dropdownWidth = dropdown.offsetWidth;
  const viewportWidth = window.innerWidth;
  const triggerCenter = triggerRect.left + triggerRect.width / 2;

  let dropdownLeft = triggerCenter - dropdownWidth / 2;
  if (dropdownLeft + dropdownWidth > viewportWidth - VIEWPORT_EDGE_MARGIN) {
    dropdownLeft = viewportWidth - VIEWPORT_EDGE_MARGIN - dropdownWidth;
  } else if (dropdownLeft < VIEWPORT_EDGE_MARGIN) {
    dropdownLeft = VIEWPORT_EDGE_MARGIN;
  }

  dropdownStyle.value = {
    left: `${dropdownLeft - rootRect.left}px`,
    right: "auto",
    transform: "none",
    "--arrow-left": `${triggerCenter - dropdownLeft}px`,
  };
};

const onDocPointerDown = (e) => {
  const root = rootRef.value;
  if (!root || !dropdownOpen.value) return;
  if (!root.contains(e.target)) closeDropdown();
};

watch(dropdownOpen, async (open) => {
  if (!open) return;
  await nextTick();
  alignDropdown();
});

onMounted(() => {
  document.addEventListener("mousedown", onDocPointerDown);
  window.addEventListener("resize", () => {
    if (dropdownOpen.value) alignDropdown();
  });
});

onUnmounted(() => {
  document.removeEventListener("mousedown", onDocPointerDown);
});
</script>

<style lang="less" scoped>
.lang-switcher {
  position: relative;
  z-index: 99999;
}

.lang-switcher-trigger {
  width: 24px;
  height: 24px;
  background: url("../assets/images/header/lang.png") center / contain no-repeat;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.06);
  }
}

.lang-switcher-dropdown {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  right: auto;
  transform: translateX(-50%);
  --arrow-left: 50%;
  min-width: 140px;
  padding: 16px 0;
  margin: 0;
  list-style: none;
  text-align: center;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 12px;
  z-index: 999;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: var(--arrow-left);
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 5px solid rgba(0, 0, 0, 0.65);
  }
}

.lang-switcher-option {
  padding: 10px 20px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  }

  &.is-active {
    color: rgba(255, 250, 180, 0.9);
  }

  &:hover {
    color: #fff;
  }
}
</style>
